type Notice = {
  id: string;
  revision: number;
  title: string;
  summary: string;
  mode: "badge" | "snippet" | "full";
  size: "small" | "large";
};
type PostState = {
  clientId: string | null;
  unreadCount: number;
  notification: Notice | null;
};
export function parsePostState(value: unknown): PostState | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (
    v.type !== "nest-support-widget:posts:state" ||
    (v.clientId !== null && typeof v.clientId !== "string") ||
    typeof v.unreadCount !== "number" ||
    !Number.isInteger(v.unreadCount) ||
    v.unreadCount < 0
  )
    return null;
  const n = v.notification;
  if (n !== null) {
    if (!n || typeof n !== "object") return null;
    const p = n as Record<string, unknown>;
    if (
      typeof p.id !== "string" ||
      !/^[a-zA-Z0-9_-]{1,100}$/.test(p.id) ||
      typeof p.revision !== "number" ||
      !Number.isInteger(p.revision) ||
      p.revision < 1 ||
      typeof p.title !== "string" ||
      p.title.length > 160 ||
      typeof p.summary !== "string" ||
      p.summary.length > 300 ||
      !["badge", "snippet", "full"].includes(String(p.mode)) ||
      !["small", "large"].includes(String(p.size))
    )
      return null;
  }
  return {
    clientId: v.clientId as string | null,
    unreadCount: Math.min(9999, v.unreadCount),
    notification: n as Notice | null,
  };
}
export function createPostNotifications(options: {
  send: (message: unknown) => void;
  available: () => boolean;
  panelOpen: () => boolean;
  onUnread: (count: number) => void;
}) {
  let state: PostState | null = null;
  let element: HTMLElement | null = null;
  let shownId: string | null = null;
  let openedOnce = false;
  function remove() {
    element?.remove();
    element = null;
  }
  function send(action: "open" | "shown" | "dismiss", id: string) {
    options.send({
      type: `nest-support-widget:posts:${action}`,
      clientId: state?.clientId,
      postId: id,
    });
  }
  function refresh() {
    const notice = state?.notification;
    if (
      !notice ||
      !options.available() ||
      document.visibilityState === "hidden" ||
      options.panelOpen()
    ) {
      remove();
      return;
    }
    const focused = document.activeElement;
    const mode = notice.mode === "full" && openedOnce ? "snippet" : notice.mode;
    if (
      mode === "full" &&
      focused instanceof HTMLElement &&
      (focused.matches("input, textarea, select") || focused.isContentEditable)
    ) {
      remove();
      return;
    }
    if (mode === "full") {
      remove();
      openedOnce = true;
      state!.notification = null;
      send("open", notice.id);
      return;
    }
    if (shownId !== notice.id) {
      shownId = notice.id;
      send("shown", notice.id);
    }
    if (mode === "badge") {
      remove();
      return;
    }
    if (element?.dataset.postId === notice.id) return;
    remove();
    element = document.createElement("section");
    element.dataset.postId = notice.id;
    element.id = "nest-support-post-notification";
    element.setAttribute("aria-label", "お知らせ");
    Object.assign(element.style, {
      position: "fixed",
      left: "16px",
      bottom: "84px",
      zIndex: "2147483647",
      width: "min(360px, calc(100vw - 32px))",
      borderRadius: "12px",
      background: "#fff",
      color: "#0f172a",
      border: "1px solid #e2e8f0",
      boxShadow: "0 12px 40px #0002",
      font: "14px/1.6 sans-serif",
    });
    const open = document.createElement("button");
    open.type = "button";
    Object.assign(open.style, {
      display: "block",
      width: "100%",
      padding: "18px 48px 18px 18px",
      textAlign: "left",
      border: "none",
      borderRadius: "12px",
      background: "transparent",
      color: "inherit",
      cursor: "pointer",
      font: "inherit",
    });
    const title = document.createElement("strong");
    title.textContent = notice.title;
    title.style.display = "block";
    const summary = document.createElement("span");
    summary.textContent = notice.summary;
    Object.assign(summary.style, {
      display: "block",
      marginTop: "4px",
      fontSize: "13px",
      color: "#64748b",
      overflowWrap: "anywhere",
    });
    open.append(title, summary);
    open.onclick = () => {
      openedOnce = true;
      state!.notification = null;
      remove();
      send("open", notice.id);
    };
    const close = document.createElement("button");
    close.type = "button";
    close.textContent = "×";
    close.setAttribute("aria-label", "お知らせを閉じる");
    Object.assign(close.style, {
      position: "absolute",
      top: "8px",
      right: "8px",
      width: "32px",
      height: "32px",
      border: "none",
      background: "transparent",
      color: "inherit",
      fontSize: "24px",
      cursor: "pointer",
    });
    close.onclick = () => {
      state!.notification = null;
      remove();
      send("dismiss", notice.id);
    };
    element.append(open, close);
    document.body.append(element);
  }
  return {
    receive(next: PostState) {
      state = next;
      options.onUnread(next.unreadCount);
      refresh();
    },
    refresh,
    markOpened() {
      openedOnce = true;
    },
    reset() {
      state = null;
      shownId = null;
      openedOnce = false;
      remove();
      options.onUnread(0);
    },
  };
}
